import {
    from,
    tap,
    mergeMap,
    share,
    Observable,
    BehaviorSubject,
    ReplaySubject,
    finalize,
    catchError,
    throwError,
    lastValueFrom,
} from 'rxjs';
import type { TypedGraphQLQueryClient } from '@precon-tools-suite-by-shared/shared-graphql-client-js-generic';
import {
    DownloadableFile,
    downloadableFilesIterator,
} from './get-downloadable-file';
import {
    DownloadMessage_Downloading,
    SmallDownloadMessage,
    createObservableDownload,
    downloadOnSuccessMessage,
    omitBlobs,
} from './download-file';

export type FileStateMessage_Idle = {
    message: 'idle';
    file: { id: string };
};
export type FileStateMessage_UrlRequesting = {
    message: 'url:requesting';
    file: { id: string };
};
export type FileStateMessage_Error = {
    message: 'error';
    file: DownloadableFile;
    error: unknown;
};

export type FileStateMessage =
    | FileStateMessage_Idle
    | FileStateMessage_UrlRequesting
    | SmallDownloadMessage
    | FileStateMessage_Error;

export type NodeState = 'idle' | 'loading' | 'error' | 'success' | 'empty';

export type NodeFilesMessage = {
    files: FileStateMessage[];
    state: NodeState;
    total: number | null;
    loaded: number;
};

export class FileDownloadManager {
    private downloads: Map<string, Observable<SmallDownloadMessage>>;
    private files: Map<string, BehaviorSubject<FileStateMessage>>;
    private nodeFileIds: Map<string, ReplaySubject<string>>;

    constructor(protected readonly client: TypedGraphQLQueryClient) {
        this.downloads = new Map();
        this.files = new Map();
        this.nodeFileIds = new Map();
    }

    protected initNodeFileIds(id: string): ReplaySubject<string> {
        const oldSubject = this.nodeFileIds.get(id);
        if (oldSubject) oldSubject.complete();

        const subject = new ReplaySubject<string>();
        this.nodeFileIds.set(id, subject);
        return subject;
    }

    protected getNodeFileIds(id: string): ReplaySubject<string> {
        return this.nodeFileIds.get(id) ?? this.initNodeFileIds(id);
    }

    protected getFileSubject(id: string): BehaviorSubject<FileStateMessage> {
        const subject = this.files.get(id);
        if (subject) return subject;

        const newSubject = new BehaviorSubject<FileStateMessage>({
            message: 'idle',
            file: { id },
        });
        this.files.set(id, newSubject);
        return newSubject;
    }

    public getFile(id: string): Observable<FileStateMessage> {
        return this.getFileSubject(id).asObservable();
    }

    protected deleteFile(id: string): void {
        const subject = this.files.get(id);
        if (!subject) return;

        subject.complete();
        this.files.delete(id);
    }

    public deleteNode(id: string): void {
        const fileIds = this.getNodeFileIds(id);

        fileIds.subscribe({
            next: (fileId) => this.deleteFile(fileId),
        });
        fileIds.complete();

        this.nodeFileIds.delete(id);
    }

    public getNode(id: string): Observable<NodeFilesMessage> {
        return new Observable((observer) => {
            const files: Map<string, FileStateMessage> = new Map();

            function update() {
                const fileStates = [...files.values()];

                let state: NodeState = 'empty';
                let loaded = 0;
                let total: number | null = 0;

                fileStates.forEach((msg) => {
                    switch (msg.message) {
                        case 'download:downloading':
                            state = 'loading';
                            loaded += (msg as DownloadMessage_Downloading)
                                .progress.loaded;
                            if (total !== null) {
                                const fileTotal =
                                    (msg as DownloadMessage_Downloading)
                                        .progress.total ?? null;
                                if (fileTotal === null) {
                                    total = null;
                                } else {
                                    total += fileTotal;
                                }
                            }
                            break;
                        case 'download:requesting':
                            state = 'loading';
                            total = null;
                            break;
                        case 'url:requesting':
                            state = 'loading';
                            total = null;
                            break;
                        case 'download:success':
                            if (state === 'empty') state = 'success';
                            break;
                        case 'idle':
                            if (state === 'empty') state = 'idle';
                            if (state === 'success') state = 'idle';
                            break;
                        case 'error':
                            if (state === 'empty') state = 'error';
                            if (state === 'success') state = 'error';
                            if (state === 'idle') state = 'error';
                            break;
                    }
                });

                observer.next({
                    files: fileStates,
                    state,
                    loaded,
                    total,
                });
            }

            const fileIdSubscription = this.getNodeFileIds(id)
                .pipe(mergeMap((fileId) => this.getFileSubject(fileId)))
                .subscribe({
                    next: (msg) => {
                        files.set(msg.file.id, msg);
                        update();
                    },
                    error: (err) => observer.error(err),
                    complete: () => observer.complete(),
                });

            return () => {
                fileIdSubscription.unsubscribe();
            };
        });
    }

    private createDownloadObservable(
        file: DownloadableFile
    ): Observable<SmallDownloadMessage> {
        return createObservableDownload(file).pipe(
            downloadOnSuccessMessage(),
            omitBlobs(),
            tap((msg) => this.getFileSubject(msg.file.id).next(msg)),
            share(),
            finalize(() => this.downloads.delete(file.id)),
            catchError((error) => {
                this.getFileSubject(file.id).next({
                    message: 'error',
                    file,
                    error,
                });

                return throwError(() => error);
            })
        );
    }

    private getDownloadObservable(
        file: DownloadableFile
    ): Observable<SmallDownloadMessage> {
        const observable =
            this.downloads.get(file.id) ?? this.createDownloadObservable(file);
        this.downloads.set(file.id, observable);
        return observable;
    }

    private createObservable(id: string): Observable<SmallDownloadMessage> {
        return from(downloadableFilesIterator(this.client, id)).pipe(
            tap((file) => this.getNodeFileIds(id).next(file.id)),
            mergeMap((file) => this.getDownloadObservable(file))
        );
    }

    public async download(id: string): Promise<void> {
        await lastValueFrom(this.createObservable(id));
    }
}
